import { Grid, GridItem, Input, InputGroup, InputLeftAddon, SimpleGrid, Switch } from '@chakra-ui/react'
import React, { useMemo } from 'react'

import { FormField } from '../../../../components/FormField'
import { MoneyInput } from '../../../../components/MaskedInput'
import { SearchableSelect } from '../../../../components/Select'
import { calculatePmt } from '../../../../helpers'
import { errorsOfField } from '../../../../helpers/tabErrorUtils'
import { IFormData } from './CoursesOnlineModal'

/** Limite de cursos do pacote, contando o próprio curso (validado também na API). */
export const PACKAGE_MAX_COURSES = 4

interface IProps {
  formData: IFormData
  setFormData: React.Dispatch<React.SetStateAction<IFormData>>
  errors: Record<string, string[]>
  courses: { id: number; name: string }[]
  installmentConfig: { monthly_rate: number; installments: number }
  disabled?: boolean
}

const toReais = (value: number) => value.toFixed(2).replace('.', ',')

/**
 * Pacote "Passaporte" do curso gravado, da campanha Monte seu Kit (ABRA-406). Ligado e com link, preço
 * e cursos, o site mostra o card do Passaporte ao lado do card do curso; sem eles, só o do curso. O
 * link Eduzz do curso avulso fica na aba Geral.
 */
export function CourseOnlinePassaporteTab({
  formData,
  setFormData,
  errors,
  courses,
  installmentConfig,
  disabled
}: IProps) {
  const isFull = formData.package_course_ids.length >= PACKAGE_MAX_COURSES
  // As parcelas do pacote seguem a mesma regra do preço do curso (config/pricing.php na API).
  const { installment, totalInstallments } = useMemo(() => {
    const installment = calculatePmt(
      installmentConfig.monthly_rate,
      installmentConfig.installments,
      formData.package_price ?? 0
    )
    return { installment, totalInstallments: installment * installmentConfig.installments }
  }, [formData.package_price, installmentConfig])
  // O SearchableSelect mostra os escolhidos na ordem das opções, e não na do valor. Os cursos do
  // pacote vêm primeiro, na ordem salva, para a tela (e o próximo onChange) manter a ordem do card.
  const packageOptions = useMemo(() => {
    const position = (id: number) => {
      const index = formData.package_course_ids.indexOf(id)
      return index === -1 ? courses.length : index
    }
    return [...courses].sort((a, b) => position(a.id) - position(b.id))
  }, [courses, formData.package_course_ids])

  return (
    <Grid templateColumns="repeat(2, 1fr)" gap={6}>
      <GridItem colSpan={2}>
        <FormField
          label="Exibir o Passaporte no site"
          htmlFor="package_enabled"
          error={errors.package_enabled}
          helperText="Desligado, o card do Passaporte some da página do curso no site. O link, o preço e os cursos continuam salvos."
          isDisabled={disabled}
        >
          <Switch
            id="package_enabled"
            isChecked={formData.package_enabled}
            onChange={(e) => setFormData((prev) => ({ ...prev, package_enabled: e.target.checked }))}
          />
        </FormField>
      </GridItem>

      <GridItem colSpan={2}>
        <FormField
          label="Nome do passaporte"
          htmlFor="package_name"
          error={errors.package_name}
          helperText='Vai como curso do lead na tabela de leads. Vazio, o lead vai como "Passaporte".'
          isDisabled={disabled}
        >
          <Input
            id="package_name"
            name="package_name"
            value={formData.package_name}
            maxLength={100}
            onChange={(e) => setFormData((prev) => ({ ...prev, package_name: e.target.value }))}
            placeholder="Ex.: Passaporte Artista Plástico"
          />
        </FormField>
      </GridItem>

      <GridItem colSpan={2}>
        <FormField
          label="URL Eduzz do pacote"
          htmlFor="eduzz_package_url"
          error={errors.eduzz_package_url}
          helperText="O link do Passaporte deste curso. Sem link, preço e cursos, o site não mostra o card do Passaporte."
          isDisabled={disabled}
        >
          <Input
            id="eduzz_package_url"
            name="eduzz_package_url"
            value={formData.eduzz_package_url}
            onChange={(e) => setFormData((prev) => ({ ...prev, eduzz_package_url: e.target.value }))}
            placeholder="https://www.eduzz.com/..."
          />
        </FormField>
      </GridItem>

      <GridItem colSpan={2}>
        <FormField
          label="Cursos do pacote"
          error={errorsOfField(errors, 'package_course_ids')}
          helperText={`Até ${PACKAGE_MAX_COURSES} cursos, contando este. A ordem é a do card no site.`}
          isDisabled={disabled}
        >
          <SearchableSelect<number>
            isMulti
            options={packageOptions.map((course) => ({
              value: course.id,
              label: course.name,
              isDisabled: isFull && !formData.package_course_ids.includes(course.id)
            }))}
            value={formData.package_course_ids}
            onChange={(ids) => setFormData((prev) => ({ ...prev, package_course_ids: ids }))}
            placeholder="Selecione os cursos"
            entity="curso"
          />
        </FormField>
      </GridItem>

      <GridItem colSpan={2}>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <GridItem>
            <FormField
              label="Preço do pacote (à vista)"
              htmlFor="package_price"
              error={errors.package_price}
              isDisabled={disabled}
            >
              <MoneyInput
                id="package_price"
                name="package_price"
                value={formData.package_price}
                onChange={(package_price) => setFormData((prev) => ({ ...prev, package_price }))}
                isDisabled={disabled}
              />
            </FormField>
          </GridItem>
          <GridItem>
            <FormField label="Parcelado" htmlFor="package_credit_price">
              <InputGroup>
                <InputLeftAddon>R$</InputLeftAddon>
                <Input id="package_credit_price" value={toReais(totalInstallments)} readOnly />
              </InputGroup>
            </FormField>
          </GridItem>
          <GridItem>
            <FormField label={`Parcela (${installmentConfig.installments}x)`} htmlFor="package_installment">
              <InputGroup>
                <InputLeftAddon>R$</InputLeftAddon>
                <Input id="package_installment" value={toReais(installment)} readOnly />
              </InputGroup>
            </FormField>
          </GridItem>
        </SimpleGrid>
      </GridItem>
    </Grid>
  )
}
